/**
 * El Ciruelo Theme — theme.js
 * ─────────────────────────────
 * 1. Cart Drawer
 * 2. Mobile Nav
 * 3. Accordion
 * 4. Product Gallery
 * 5. Variant Picker
 * 6. Scroll Reveal
 * 7. Announcement Carousel
 * 8. Sticky Header
 * 9. Quick Add
 */

/* ─────────────────────────────────────────
   1. CART DRAWER
───────────────────────────────────────── */
class CartDrawer {
  constructor() {
    this.drawer   = document.querySelector('.cart-drawer');
    this.overlay  = document.querySelector('.cart-drawer__overlay');
    this.panel    = document.querySelector('.cart-drawer__panel');
    this.closeBtn = document.querySelector('.cart-drawer__close');
    this.cartCount = document.querySelectorAll('.cart-count');

    if (!this.drawer) return;
    this.bindEvents();
  }

  bindEvents() {
    // Open triggers
    document.querySelectorAll('[data-cart-open]').forEach(el => {
      el.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });
    // Close
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // Listen for cart:open events
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.fetchCart());
  }

  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.fetchCart();
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async fetchCart() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
      this.updateCount(cart.item_count);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  updateCount(count) {
    this.cartCount.forEach(el => {
      el.textContent = count;
      el.style.display = count === 0 ? 'none' : '';
    });
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: 'MXN', minimumFractionDigits: 2
    }).format(cents / 100);
  }

  render(cart) {
    const body   = document.querySelector('.cart-drawer__body');
    const footer = document.querySelector('.cart-drawer__footer');
    if (!body) return;

    // Free shipping progress
    const threshold = parseFloat(document.querySelector('[data-shipping-threshold]')?.dataset.shippingThreshold || '50000');
    const pct = Math.min((cart.total_price / threshold) * 100, 100);
    const remaining = threshold - cart.total_price;

    let itemsHtml = '';
    if (cart.item_count === 0) {
      itemsHtml = `
        <div style="text-align:center; padding: 60px 0; color: var(--color-muted);">
          <p style="font-family: var(--font-display); font-size: 1.4rem; color: var(--color-forest); margin-bottom: 12px;">Tu carrito está vacío</p>
          <a href="/collections/all" class="btn btn--primary btn--sm" onclick="document.querySelector('.cart-drawer').classList.remove('is-open')">Explorar productos</a>
        </div>`;
    } else {
      cart.items.forEach(item => {
        itemsHtml += `
          <div class="cart-item" data-key="${item.key}">
            <div class="cart-item__img-wrap">
              <img class="cart-item__img" src="${item.image}" alt="${item.title}" width="80" height="80" loading="lazy">
            </div>
            <div class="cart-item__info">
              <div class="cart-item__name">${item.product_title}</div>
              ${item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
              <div class="cart-item__price-row">
                <span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span>
                <button class="cart-item__remove" onclick="CartDrawerInstance.removeItem('${item.key}')">Eliminar</button>
              </div>
            </div>
          </div>`;
      });
    }

    body.innerHTML = `
      <div class="shipping-progress">
        <p class="shipping-progress__text">
          ${pct >= 100
            ? '🎉 ¡Envío gratis aplicado!'
            : `Agrega ${this.formatMoney(remaining)} más para <strong>envío gratis</strong>`}
        </p>
        <div class="shipping-progress__bar">
          <div class="shipping-progress__fill" style="width:${pct}%"></div>
        </div>
      </div>
      ${itemsHtml}`;

    if (footer) {
      footer.innerHTML = cart.item_count > 0 ? `
        <div class="cart-subtotal">
          <span class="cart-subtotal__label">Subtotal</span>
          <span class="cart-subtotal__price">${this.formatMoney(cart.total_price)}</span>
        </div>
        <p class="cart-subtotal__note">Impuestos e envío calculados al finalizar</p>
        <a href="/checkout" class="btn btn--primary btn--full btn--lg">Finalizar compra</a>
        <a href="/cart" class="btn btn--secondary btn--full">Ver carrito</a>` : '';
    }
  }

  async removeItem(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    this.fetchCart();
  }

  async addItem(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (res.ok) {
      document.dispatchEvent(new Event('cart:open'));
      document.dispatchEvent(new Event('cart:refresh'));
    }
    return res.json();
  }
}

const CartDrawerInstance = new CartDrawer();
window.CartDrawerInstance = CartDrawerInstance;

/* ─────────────────────────────────────────
   2. MOBILE NAV
───────────────────────────────────────── */
class MobileNav {
  constructor() {
    this.nav      = document.querySelector('.mobile-nav');
    this.toggle   = document.querySelector('.header-menu-toggle');
    this.overlay  = document.querySelector('.mobile-nav__overlay');
    if (!this.nav) return;
    this.toggle?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  }
  open() { this.nav.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
  close() { this.nav.classList.remove('is-open'); document.body.style.overflow = ''; }
}
new MobileNav();

/* ─────────────────────────────────────────
   3. ACCORDION
───────────────────────────────────────── */
document.querySelectorAll('.accordion__trigger').forEach(trigger => {
  trigger.addEventListener('click', () => {
    const item = trigger.closest('.accordion__item');
    const isOpen = item.classList.contains('is-open');
    // Close all in same parent
    trigger.closest('.accordion').querySelectorAll('.accordion__item').forEach(i => i.classList.remove('is-open'));
    if (!isOpen) item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', !isOpen);
  });
});

/* ─────────────────────────────────────────
   4. PRODUCT GALLERY
───────────────────────────────────────── */
class ProductGallery {
  constructor(el) {
    this.el     = el;
    this.main   = el.querySelector('.product-gallery__main');
    this.mainImg = el.querySelector('.product-gallery__main img');
    this.thumbs = el.querySelectorAll('.product-gallery__thumb');
    this.bindEvents();
  }

  bindEvents() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(thumb, i));
    });
  }

  setActive(thumb, i) {
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    const src = thumb.querySelector('img')?.src;
    if (src && this.mainImg) {
      this.mainImg.src = src.replace('_100x100', '_1200x1200').replace('_80x80', '_1200x1200');
    }
  }
}

document.querySelectorAll('.product-gallery').forEach(el => new ProductGallery(el));

/* ─────────────────────────────────────────
   5. VARIANT PICKER
───────────────────────────────────────── */
class VariantPicker {
  constructor(el) {
    this.form       = el;
    this.productId  = el.dataset.productId;
    this.variants   = JSON.parse(el.querySelector('[data-variants-json]')?.textContent || '[]');
    this.selects    = el.querySelectorAll('[data-option-index]');
    this.priceEl    = document.querySelector('.price__current');
    this.compareEl  = document.querySelector('.price__compare');
    this.submitBtn  = el.querySelector('[type="submit"]');
    this.hiddenInput = el.querySelector('[name="id"]');
    this.bindEvents();
  }

  bindEvents() {
    this.selects.forEach(select => {
      select.addEventListener('change', () => this.onSelect());
    });
    // Swatch clicks
    this.form.querySelectorAll('.swatch').forEach(sw => {
      sw.addEventListener('click', () => {
        const list = sw.closest('.swatch-list');
        list.querySelectorAll('.swatch').forEach(s => s.classList.remove('is-active'));
        sw.classList.add('is-active');
        // Sync hidden select
        const select = this.form.querySelector(`[data-option-index="${sw.dataset.optionIndex}"]`);
        if (select) { select.value = sw.dataset.value; }
        this.onSelect();
      });
    });
    // Variant buttons
    this.form.querySelectorAll('.variant-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const list = btn.closest('.variant-button-list');
        list.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        const select = this.form.querySelector(`[data-option-index="${btn.dataset.optionIndex}"]`);
        if (select) { select.value = btn.dataset.value; }
        this.onSelect();
      });
    });
  }

  getSelected() {
    return Array.from(this.selects).map(s => s.value);
  }

  findVariant(options) {
    return this.variants.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );
  }

  onSelect() {
    const options = this.getSelected();
    const variant = this.findVariant(options);
    if (!variant) return;

    // Update price
    if (this.priceEl) {
      this.priceEl.textContent = this.formatMoney(variant.price);
    }
    if (this.compareEl) {
      if (variant.compare_at_price > variant.price) {
        this.compareEl.textContent = this.formatMoney(variant.compare_at_price);
        this.compareEl.style.display = '';
      } else {
        this.compareEl.style.display = 'none';
      }
    }

    // Update hidden input
    if (this.hiddenInput) this.hiddenInput.value = variant.id;

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Update submit button
    if (this.submitBtn) {
      if (!variant.available) {
        this.submitBtn.textContent = 'Agotado';
        this.submitBtn.disabled = true;
      } else {
        this.submitBtn.textContent = 'Agregar al carrito';
        this.submitBtn.disabled = false;
      }
    }

    // Update gallery if variant has image
    if (variant.featured_image) {
      const thumb = document.querySelector(`[data-variant-image="${variant.id}"]`);
      if (thumb) thumb.click();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: 'MXN', minimumFractionDigits: 2
    }).format(cents / 100);
  }
}

document.querySelectorAll('[data-product-form]').forEach(el => new VariantPicker(el));

/* Product form submit */
document.querySelectorAll('[data-product-form]').forEach(form => {
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const variantId = form.querySelector('[name="id"]')?.value;
    const qty = parseInt(form.querySelector('[name="quantity"]')?.value || 1);
    if (!variantId) return;

    btn.textContent = 'Agregando...';
    btn.disabled = true;
    await CartDrawerInstance.addItem(variantId, qty);
    btn.textContent = '✓ Agregado';
    setTimeout(() => {
      btn.textContent = 'Agregar al carrito';
      btn.disabled = false;
    }, 1500);
  });
});

/* ─────────────────────────────────────────
   6. SCROLL REVEAL
───────────────────────────────────────── */
const revealObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry, i) => {
    if (entry.isIntersecting) {
      setTimeout(() => {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }, i * 60);
    }
  });
}, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

document.querySelectorAll('.animate-fade-up, .stagger-children').forEach(el => {
  revealObserver.observe(el);
});

/* ─────────────────────────────────────────
   7. STICKY HEADER
───────────────────────────────────────── */
const header = document.querySelector('.site-header--sticky');
if (header) {
  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    if (y > 80) {
      header.style.boxShadow = '0 2px 20px rgba(44,61,46,0.08)';
    } else {
      header.style.boxShadow = '';
    }
    lastY = y;
  }, { passive: true });
}

/* ─────────────────────────────────────────
   8. QUICK ADD
───────────────────────────────────────── */
document.querySelectorAll('[data-quick-add]').forEach(btn => {
  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    const variantId = btn.dataset.quickAdd;
    if (!variantId) return;
    btn.textContent = '...';
    await CartDrawerInstance.addItem(variantId, 1);
    btn.textContent = '✓';
    setTimeout(() => { btn.textContent = 'Agregar'; }, 1500);
  });
});

/* ─────────────────────────────────────────
   9. ANNOUNCEMENT CAROUSEL
───────────────────────────────────────── */
class AnnouncementCarousel {
  constructor(el) {
    this.el    = el;
    this.items = el.querySelectorAll('.announcement-bar__item');
    this.index = 0;
    if (this.items.length <= 1) return;
    this.items[0].style.display = '';
    this.interval = setInterval(() => this.next(), 4000);
  }
  next() {
    this.items[this.index].style.opacity = '0';
    setTimeout(() => {
      this.items[this.index].style.display = 'none';
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].style.display = '';
      this.items[this.index].style.opacity = '0';
      setTimeout(() => { this.items[this.index].style.opacity = '1'; }, 10);
    }, 300);
  }
}
document.querySelectorAll('.announcement-bar--carousel').forEach(el => new AnnouncementCarousel(el));

/* ─────────────────────────────────────────
   10. SHOPIFY THEME EDITOR — SECTION RELOAD
   Re-initializes JS features when the theme editor re-renders a section,
   preventing animate-fade-up elements from staying invisible (opacity:0).
───────────────────────────────────────── */
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;

    // Make all scroll-reveal elements visible immediately in the editor
    section.querySelectorAll('.animate-fade-up, .stagger-children').forEach(el => {
      el.classList.add('is-visible');
      revealObserver.unobserve(el);
    });

    // Re-bind accordion triggers inside the reloaded section
    section.querySelectorAll('.accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item   = trigger.closest('.accordion__item');
        const isOpen = item.classList.contains('is-open');
        trigger.closest('.accordion').querySelectorAll('.accordion__item').forEach(i => {
          i.classList.remove('is-open');
          i.querySelector('.accordion__trigger')?.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Re-init announcement carousel
    section.querySelectorAll('.announcement-bar--carousel').forEach(el => new AnnouncementCarousel(el));

    // Re-init product gallery
    section.querySelectorAll('.product-gallery').forEach(el => new ProductGallery(el));
  });
}
